import { useQuery } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import React, { useCallback, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import BillPayBankAccounts, {
  type BillPayFundingAccount,
  type BillPayKybStatus,
} from 'src/feature/BillPayBankAccounts/BillPayBankAccounts'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
// Payments onboarding building blocks (EX-40413). These live under
// page-components/OrganizationBankAccount/ but are no longer a page of their
// own — the Bill Pay Bank Account surface is unified here. They are the KYB
// verification flow + the Modern Treasury embedded connect + the pure state
// helpers that drive both cards.
import {
  canLaunchFundingAccountFlow,
  fundingAccountOf,
  fundingAccountStageFromStatus,
  kybStageFromOnboarding,
  legalBusinessNameOf,
  onboardingStatusOf,
} from 'src/page-components/OrganizationBankAccount/bankAccountOnboarding'
import { KybVerificationModal } from 'src/page-components/OrganizationBankAccount/KybVerificationModal'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  paymentsQueries,
  useCompleteAccountCollectionFlow,
  useCompleteFundingAccountVerification,
  useCreateAccountCollectionFlow,
  useInitiateFundingAccountVerification,
  useSubmitLegalEntity,
} from 'src/queries/payments/payments.queries'
import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
} from 'src/queries/payments/payments.types'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { canManageBillPay } from 'src/utils/billPayAccess'
import { isBillPayAchEnabled } from 'src/utils/featureFlags'

import FundingAccountVerificationModal from './FundingAccountVerificationModal'

type Translate = ReturnType<typeof useTranslate>

const errorStatusOf = (error: unknown): number | undefined =>
  error && typeof error === 'object' && 'status' in error
    ? (error.status as number | undefined)
    : undefined

const errorCodeOf = (error: unknown): string =>
  error && typeof error === 'object'
    ? 'code' in error
      ? String(error.code).toUpperCase()
      : 'error' in error
        ? String(error.error).toUpperCase()
        : ''
    : ''

const fundingVerificationStartErrorMessage = (translate: Translate): string =>
  translate('We could not start verification. Please try again.')

const fundingVerificationCompletionErrorMessage = (
  error: unknown,
  translate: Translate,
): string => {
  const status = errorStatusOf(error)
  const code = errorCodeOf(error)

  if (code.includes('VERIFICATION_ACTION_REQUIRED')) {
    return translate(
      'Verification needs to be restarted. Close this dialog and retry verification.',
    )
  }

  if (code.includes('MISMATCH') || code.includes('INCORRECT')) {
    return translate(
      'Those amounts did not match. Check your bank account and try again.',
    )
  }

  if (status === 400 || code.includes('INVALID')) {
    return translate('Enter two numerical deposit amounts in dollars.')
  }

  return translate('We could not verify your bank account. Please try again.')
}

/**
 * BankAccountsPage — Bill Pay Milestone 1: the org's single "Bank Account"
 * settings surface (Settings → General → Bank Account,
 * `/organizationSettings/:id/bankaccounts`).
 *
 * Unifies two parallel efforts (#7307 + #7304): it renders Richa's two-card,
 * KYB-gated layout (the routing-free `BillPayBankAccounts` feature) and drives
 * it from the real payments onboarding data + flows (the payments connection /
 * internal-accounts queries, the KYB verification modal, and Modern Treasury's
 * embedded Account Collection Flow).
 *
 * Gating:
 *  - Feature flag `bill_pay_ach` (default off), OR the org id is in the
 *    `bill_pay_ach_whitelist` remote-config allowlist (per-org pilot rollout).
 *  - Role: owner/admin + full-visibility bookkeepers (EX-40473), matching the
 *    backend `managepayments` permission and payments realtime recipients.
 *  - Tier: Pro/Enterprise only, via the `VIEW_PAYABLES` UserFeature. The backend gates it
 *    to fully-paid Pro/Ent (in-trial + saasless resolve false), so the FE just checks the
 *    one boolean. Starter / in-trial / saasless → NotFound today. TODO(bill-pay upsell):
 *    Starter should get an empty/upsell state (Melissa) once copy + illustration land
 *    (Richa Figma; ship-now-vs-punt-past-M3 still open).
 */
const BankAccountsPage: React.FC = () => {
  const translate = useTranslate()
  const currentUser = useCurrentUser()
  const appName = useSelector(selectAppName)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)

  // VIEW_PAYABLES is the Pro/Enterprise tier gate. The backend resolves it to
  // fully-paid Pro/Ent (in-trial + saasless → false), so the FE just checks the
  // one boolean. Needed for every user once the remote-config boundary is on;
  // while bill_pay_ach is off the /features request stays disabled.
  const billPayAchEnabled = isBillPayAchEnabled(remoteConfig, organizationId)
  const { data: userFeatures, isLoading: userFeaturesLoading } =
    useQueryUserFeatures(
      [UserFeature.VIEW_PAYABLES, UserFeature.ORGANIZATION_USER_STATISTICS],
      {
        enabled: billPayAchEnabled,
      },
    )

  const canViewPayables = userFeatures?.[UserFeature.VIEW_PAYABLES] ?? false
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so this
  // gate and the nav item can never diverge — see src/utils/billPayAccess.
  const hasAccess =
    billPayAchEnabled &&
    canManageBillPay({
      hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
      organizationRole: currentUser?.organizationRole,
      orgUserStatisticsEnabled:
        userFeatures?.[UserFeature.ORGANIZATION_USER_STATISTICS],
    }) &&
    canViewPayables

  // The Bill Pay boundary AND a resolved org id gate every query, so a deep
  // link with the flag off (or before the user resolves) stays silent instead
  // of hitting the dark `/payments/connection` endpoint (EX-40413 review fix).
  const paymentsReady = hasAccess && Boolean(organizationId)

  const connectionQuery = useQuery({
    ...paymentsQueries.connection(organizationId),
    enabled: paymentsReady,
    // Completion driver: the KYB connection advances on a provider webhook the
    // client never sees. The API emits a `PAYMENT_CONNECTION` Pusher event on
    // each transition; App.tsx routes it through the shared Pusher limiter and
    // circuit breaker, which fetches-and-patches this exact org key. No polling
    // — the page also refetches on window focus (React Query default).
  })
  const onboardingStatus = onboardingStatusOf(connectionQuery.data)

  // The "connected" Bank Account card is driven off the funding account the
  // backend links onto the connection once the Account Collection Flow completes
  // (`/complete`) — not inferred from `/internal-accounts` (EX-40413).
  const fundingAccount = fundingAccountOf(connectionQuery.data)
  const fundingAccountQuery = useQuery({
    ...paymentsQueries.fundingAccount(organizationId),
    enabled: paymentsReady && Boolean(fundingAccount),
  })
  // The realtime projection is authoritative only when it represents the same
  // account currently linked to this org. While it is loading or unavailable,
  // retain the durable connection status so the CTA never bounces through an
  // artificial empty state.
  const liveFundingAccount =
    fundingAccountQuery.data?.id === fundingAccount?.id
      ? fundingAccountQuery.data
      : undefined
  const fundingAccountStage = fundingAccountStageFromStatus(
    liveFundingAccount?.status ?? fundingAccount?.status,
  )
  const legalBusinessName = legalBusinessNameOf(connectionQuery.data)

  const submitLegalEntity = useSubmitLegalEntity(organizationId)
  const createAccountFlow = useCreateAccountCollectionFlow()
  const completeAccountFlow = useCompleteAccountCollectionFlow(organizationId)
  const initiateFundingVerification =
    useInitiateFundingAccountVerification(organizationId)
  const completeFundingVerification =
    useCompleteFundingAccountVerification(organizationId)

  const [kybModalOpen, setKybModalOpen] = useState(false)
  const [bankModalOpen, setBankModalOpen] = useState(false)
  const [fundingVerificationModalOpen, setFundingVerificationModalOpen] =
    useState(false)
  const [accountFlowPending, setAccountFlowPending] = useState(false)
  const [fundingVerificationStartError, setFundingVerificationStartError] =
    useState<string | null>(null)
  const [
    fundingVerificationCompletionError,
    setFundingVerificationCompletionError,
  ] = useState<string | null>(null)

  // The hold is only meaningful while the embed is open or the connection
  // refetch is still in flight. Latching it past that strands the user: an
  // empty connection means the link did not land, and they need the Add CTA
  // back rather than a card frozen on `linking` until a reload.
  const accountFlowHeld =
    accountFlowPending && (bankModalOpen || connectionQuery.isFetching)

  // Guard the embedded flow whenever any funding account already exists,
  // including PENDING_VERIFICATION and unknown future statuses. Collection is
  // not verification, but it is enough to block duplicate enrollment.
  const canLaunchAccountFlow = canLaunchFundingAccountFlow(
    connectionQuery.data,
    createAccountFlow.isPending,
    accountFlowHeld,
  )
  const launchCreateFlow = useCallback(
    (): Promise<AccountCollectionFlowResponse> =>
      canLaunchAccountFlow
        ? createAccountFlow.mutateAsync()
        : Promise.reject(
            new Error('A funding account already exists or is in progress.'),
          ),
    [canLaunchAccountFlow, createAccountFlow],
  )

  const closeKybModal = useCallback((): void => {
    setKybModalOpen(false)
    // Scrub the KYB PII (EIN/SSN/DOB/addresses/base64 docs) held as the
    // mutation's `variables` the moment the modal closes; `gcTime: 0` on the
    // mutation evicts the cache copy once it goes inactive (EX-40413 review fix).
    submitLegalEntity.reset()
  }, [submitLegalEntity])

  const closeBankModal = useCallback((): void => {
    setAccountFlowPending(false)
    setBankModalOpen(false)
  }, [])
  const handleAccountFlowTerminal = useCallback(
    (): void => setAccountFlowPending(false),
    [],
  )
  const openFundingVerificationModal = useCallback((): void => {
    setFundingVerificationCompletionError(null)
    completeFundingVerification.reset()
    setFundingVerificationModalOpen(true)
  }, [completeFundingVerification])
  const handleAccountLinked = useCallback(
    (result: AccountCollectionFlowCompletionResponse): void => {
      // Releasing the hold without a linked account re-exposes "Add bank
      // account" over an empty connection — a duplicate collection flow. It
      // stays a hold, not a latch: `accountFlowHeld` expires with the refetch.
      setAccountFlowPending(!result.fundingAccount)
      setBankModalOpen(false)
      if (result.fundingAccount?.status === 'PENDING_VERIFICATION') {
        openFundingVerificationModal()
      }
    },
    [openFundingVerificationModal],
  )
  const closeFundingVerificationModal = useCallback((): void => {
    setFundingVerificationModalOpen(false)
    setFundingVerificationCompletionError(null)
    completeFundingVerification.reset()
  }, [completeFundingVerification])
  const retryFundingVerification = useCallback((): void => {
    if (initiateFundingVerification.isPending) return
    setFundingVerificationStartError(null)
    void initiateFundingVerification
      .mutateAsync()
      .then(() => openFundingVerificationModal())
      .catch(() => {
        setFundingVerificationStartError(
          fundingVerificationStartErrorMessage(translate),
        )
      })
      .finally(() => initiateFundingVerification.reset())
  }, [initiateFundingVerification, openFundingVerificationModal, translate])
  const submitFundingVerificationAmounts = useCallback(
    async (amounts: [number, number]): Promise<void> => {
      setFundingVerificationCompletionError(null)
      try {
        await completeFundingVerification.mutateAsync({ amounts })
        setFundingVerificationModalOpen(false)
      } catch (error) {
        setFundingVerificationCompletionError(
          fundingVerificationCompletionErrorMessage(error, translate),
        )
        throw error
      } finally {
        completeFundingVerification.reset()
      }
    },
    [completeFundingVerification, translate],
  )

  if (
    !loadedRemoteConfig ||
    currentUser === undefined ||
    (billPayAchEnabled && userFeaturesLoading)
  ) {
    return <LoadingBackgroundPage />
  }

  if (!hasAccess) {
    return <NotFoundBackgroundPage />
  }

  // Hold the first render until the real onboarding status is known, so the
  // cards never flash the default `unverified` first-run state over a verified
  // org. Two cases must both wait:
  //  - the connection query's initial in-flight fetch (`isLoading`); and
  //  - the org id not yet resolved — the query is then DISABLED, so it is never
  //    `isLoading`; falling through would render the default NOT_CONNECTED
  //    first-run state and enable KYB submission before an org id even exists
  //    (EX-40413 review fix).
  if (connectionQuery.isLoading || !organizationId) {
    return <LoadingBackgroundPage />
  }

  // Collapse the onboarding state machine onto the card's status union.
  // `denied` is terminal because the API claim cannot accept a second legal
  // entity submission; `pending` covers both fresh review and SUSPENDED.
  const kybStage = kybStageFromOnboarding(onboardingStatus)
  const kybStatus: BillPayKybStatus = kybStage

  // The durable connection decides whether an account exists; the separate
  // realtime provider projection supplies its current status and display-safe
  // details. Missing provider fields render as an em-dash.
  const account: BillPayFundingAccount | null =
    fundingAccount == null
      ? accountFlowHeld || createAccountFlow.isPending
        ? { status: 'linking' }
        : null
      : {
          status:
            fundingAccountStage === 'verified'
              ? 'active'
              : fundingAccountStage === 'pending'
                ? 'pending'
                : fundingAccountStage === 'unverified'
                  ? 'unverified'
                  : 'review',
          bankName: liveFundingAccount?.bankName ?? undefined,
          accountType: liveFundingAccount?.accountType
            ? liveFundingAccount.accountType.charAt(0).toUpperCase() +
              liveFundingAccount.accountType.slice(1).toLowerCase()
            : undefined,
          routingNumber: liveFundingAccount?.routingNumber ?? undefined,
          last4: liveFundingAccount?.accountNumberLast4 ?? undefined,
        }

  return (
    <>
      <Helmet>
        <title>{`Bank Account | ${appName}`}</title>
        <meta
          name="description"
          content={`Bank Account settings page of ${appName}`}
        />
      </Helmet>
      <LayoutPage title={<span>{translate('Bank Account')}</span>}>
        {connectionQuery.isError ? (
          <AlertPanel variant="severe">
            {translate(
              'We could not load your bank account settings. Please try again.',
            )}
          </AlertPanel>
        ) : (
          <>
            {fundingVerificationStartError && (
              <AlertPanel variant="severe">
                {fundingVerificationStartError}
              </AlertPanel>
            )}
            <BillPayBankAccounts
              kybStatus={kybStatus}
              legalBusinessName={legalBusinessName}
              account={account}
              onStartVerification={(): void => setKybModalOpen(true)}
              onAddBankAccount={(): void => setBankModalOpen(true)}
              onEnterDepositAmounts={
                fundingAccountStage === 'pending'
                  ? openFundingVerificationModal
                  : undefined
              }
              onRetryFundingAccountVerification={
                fundingAccountStage === 'unverified'
                  ? retryFundingVerification
                  : undefined
              }
            />
          </>
        )}
      </LayoutPage>

      {/* KYB verification (Business Verification card CTA) */}
      <KybVerificationModal
        open={kybModalOpen}
        onClose={closeKybModal}
        onSubmit={(request): Promise<unknown> =>
          submitLegalEntity.mutateAsync(request)
        }
      />

      {/* Modern Treasury embedded bank-connect (Bank Account card CTA) */}
      <BaseModal
        isOpen={bankModalOpen}
        onClose={accountFlowPending ? (): void => undefined : closeBankModal}
        variant="interactive"
        showCloseButton={!accountFlowPending}
        aria-label={translate('Connect your bank account')}
      >
        {bankModalOpen && (
          <MtAccountCollection
            createFlow={launchCreateFlow}
            completeFlow={(
              flowId,
            ): Promise<AccountCollectionFlowCompletionResponse> =>
              completeAccountFlow.mutateAsync(flowId)
            }
            onPending={(): void => setAccountFlowPending(true)}
            onTerminal={handleAccountFlowTerminal}
            onCancel={closeBankModal}
            onLinked={handleAccountLinked}
          />
        )}
      </BaseModal>

      <FundingAccountVerificationModal
        open={fundingVerificationModalOpen}
        submitting={completeFundingVerification.isPending}
        serverError={fundingVerificationCompletionError}
        onClose={closeFundingVerificationModal}
        onSubmit={submitFundingVerificationAmounts}
      />
    </>
  )
}

export default BankAccountsPage
